import { Theme, Box, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { ManyImagesUpload } from '../../../../components';
import { postNeighborhoodHoaFiles } from '../../../../fetch';
import { INeighborhoodDetailHoaInfoImage } from '../../../../models';
import { ALLOWED_IMAGE_EXTENSIONS } from '../../../../helpers';

interface IHOAImagesProps {
  neighborhoodId: string;
  photos?: INeighborhoodDetailHoaInfoImage[];
  documentOwner:
    | 'covenantsAndRestrictions'
    | 'amendmentsToDeclarations'
    | 'architecturalGuidelines'
    | 'plat';
  label: string;
  setSelectedImage: (val: any) => void;
}

export const HoaImages: FC<IHOAImagesProps> = ({
  neighborhoodId,
  documentOwner,
  label,
  photos,
  setSelectedImage
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { setFieldValue } = useFormikContext();
  const [isUploading, setUploading] = useState(false);
  return (
    <Box className={classes.photosContainer}>
      <Box paddingRight="24px">
        <ManyImagesUpload
          imageTitle={label}
          isRequired={false}
          isDisabled={isUploading}
          isLoading={isUploading}
          imageKey={documentOwner}
          fileType={['.pdf'].concat(ALLOWED_IMAGE_EXTENSIONS).join(', ')}
          handleFileChange={async (images: any[], files: File[]) => {
            try {
              setUploading(true);
              const formData = new FormData();
              files.forEach(file => {
                formData.append('files', file);
              });
              const res = await postNeighborhoodHoaFiles(neighborhoodId, documentOwner, formData);
              // display the images
              setFieldValue(documentOwner, [...(photos || []), ...res]);
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Error uploading images, please try again.', {
                variant: 'error'
              });
            } finally {
              setUploading(false);
            }
          }}
          images={[]}
        />
      </Box>
      {photos?.length > 0 && (
        <Box className={classes.photoTable}>
          <List>
            {photos.map((photo: INeighborhoodDetailHoaInfoImage, index) => {
              return (
                <ListItem disablePadding key={`${index}`}>
                  <ListItemButton
                    sx={theme => ({
                      color: theme.palette.primary.main
                    })}
                    disabled={isUploading}
                    onClick={() => {
                      setSelectedImage(photo.downloadUrl);
                    }}
                  >
                    <ListItemText primary={photo.fileDisplayName ?? `image.png (${index + 1})`} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  photosContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    },
    '@media print': {
      display: 'block'
    }
  },
  photoTable: {
    borderTop: `2px solid ${theme.palette.secondary.main}`,
    [theme.breakpoints.up('md')]: {
      paddingLeft: '24px',
      borderLeft: `2px solid ${theme.palette.secondary.main}`,
      borderTop: 'none'
    }
  }
}));
